import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { Combobox } from '../components/Combobox';
import { FileMultiUpload } from '../components/FileMultiUpload';
import { maskDoc, digitsOnly, isValidDoc, docTipoLabel } from '../lib/cpfCnpj';
import {
  Receipt, Plus, Trash2, Pencil, Search, Filter as FilterIcon,
  Paperclip, Wallet, AlertCircle, CheckCircle2, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const formatDateBR = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch (_e) {
    return iso;
  }
};

const STATUS_OPTIONS = [
  { value: 'pago', label: 'Pago' },
  { value: 'planejado', label: 'Planejado' },
  { value: 'cancelado', label: 'Cancelado' },
];

const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'outro', label: 'Outro' },
];

const DOC_TYPES = [
  { value: 'nota_fiscal', label: 'Nota fiscal' },
  { value: 'recibo', label: 'Recibo' },
  { value: 'cupom', label: 'Cupom' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'outro', label: 'Outro' },
];

const STATUS_COLORS = {
  pago: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  planejado: 'bg-amber-100 text-amber-700 border-amber-200',
  cancelado: 'bg-slate-100 text-slate-600 border-slate-200 line-through',
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  descricao: '',
  fornecedor: '',
  fornecedor_tipo: 'pf',
  fornecedor_documento: '',
  valor: 0,
  data_pagamento: today(),
  area_id: '',
  subarea_id: '',
  periodo_id: '',
  city_group_id: '',
  estado: '',
  cidade: '',
  forma_pagamento: 'pix',
  documento_tipo: 'recibo',
  documento_numero: '',
  anexos: [],
  observacoes: '',
  status: 'pago',
});

export const FinanceiroGastosPage = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters (applied)
  const [filters, setFilters] = useState({
    search: '', area_id: '', periodo_id: '', status: '',
    data_inicio: '', data_fim: '',
  });
  const [pendingFilters, setPendingFilters] = useState({
    search: '', area_id: '', periodo_id: '', status: '',
    data_inicio: '', data_fim: '',
  });

  // Dialog state
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const tenantId = user?.tenant_id || user?.active_tenant?.id;
  const folder = tenantId ? `meadd/financeiro/${tenantId}` : null;

  useEffect(() => {
    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchPlan = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/financeiro/budget`, {
        withCredentials: true,
      });
      setPlan(data.plan);
    } catch (_err) {
      // silent
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await axios.get(`${BACKEND_URL}/api/financeiro/gastos`, {
        params, withCredentials: true,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (_err) {
      toast.error('Erro ao carregar gastos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/financeiro/gastos/summary`, {
        withCredentials: true,
      });
      setSummary(data);
    } catch (_err) {
      // silent
    }
  };

  // ---- Dialog handlers ----
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      descricao: item.descricao || '',
      fornecedor: item.fornecedor || '',
      fornecedor_tipo: item.fornecedor_tipo || 'pf',
      fornecedor_documento: item.fornecedor_documento || '',
      valor: item.valor || 0,
      data_pagamento: item.data_pagamento || today(),
      area_id: item.area_id || '',
      subarea_id: item.subarea_id || '',
      periodo_id: item.periodo_id || '',
      city_group_id: item.city_group_id || '',
      estado: item.estado || '',
      cidade: item.cidade || '',
      forma_pagamento: item.forma_pagamento || 'pix',
      documento_tipo: item.documento_tipo || 'recibo',
      documento_numero: item.documento_numero || '',
      anexos: item.anexos || [],
      observacoes: item.observacoes || '',
      status: item.status || 'pago',
    });
    setOpen(true);
  };

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.descricao.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    if (!form.valor || form.valor <= 0) {
      toast.error('Valor deve ser maior que zero');
      return;
    }
    if (form.fornecedor_documento && !isValidDoc(form.fornecedor_documento, form.fornecedor_tipo)) {
      const expected = form.fornecedor_tipo === 'pj' ? 'CNPJ (14 dígitos)' : 'CPF (11 dígitos)';
      toast.error(`${expected} inválido.`);
      return;
    }
    try {
      const payload = { ...form };
      // Normalize document to digits-only before sending
      payload.fornecedor_documento = digitsOnly(payload.fornecedor_documento) || null;
      // Clean optional empty strings to null
      ['area_id','subarea_id','periodo_id','city_group_id','estado','cidade',
        'fornecedor','fornecedor_tipo','documento_numero','observacoes','data_pagamento']
        .forEach((k) => {
          if (payload[k] === '') payload[k] = null;
        });
      // If no document, also null the type
      if (!payload.fornecedor_documento) payload.fornecedor_tipo = null;
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/financeiro/gastos/${editingId}`, payload, {
          withCredentials: true,
        });
        toast.success('Gasto atualizado.');
      } else {
        await axios.post(`${BACKEND_URL}/api/financeiro/gastos`, payload, {
          withCredentials: true,
        });
        toast.success('Gasto cadastrado.');
      }
      setOpen(false);
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar');
    }
  };

  const deleteExpense = async (item) => {
    if (!window.confirm(`Excluir "${item.descricao}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/financeiro/gastos/${item.id}`, {
        withCredentials: true,
      });
      toast.success('Gasto excluído.');
      fetchExpenses();
      fetchSummary();
    } catch (_err) {
      toast.error('Erro ao excluir');
    }
  };

  // ---- Options derived from plan ----
  const areaOptions = useMemo(
    () =>
      (plan?.areas || []).map((a) => ({
        value: a.id, label: a.name, hint: `${a.percentual || 0}%`,
      })),
    [plan]
  );

  const subareaOptions = useMemo(() => {
    if (!form.area_id) return [];
    const area = (plan?.areas || []).find((a) => a.id === form.area_id);
    return (area?.subareas || []).map((s) => ({
      value: s.id, label: s.name, hint: `${s.percentual || 0}%`,
    }));
  }, [plan, form.area_id]);

  const periodoOptions = useMemo(
    () =>
      (plan?.periods || []).map((p) => ({
        value: p.id, label: p.name, hint: `${p.percentual || 0}%`,
      })),
    [plan]
  );

  const cityGroupOptions = useMemo(
    () =>
      (plan?.city_groups || []).map((g) => ({
        value: g.id, label: g.name, hint: `${g.cidades?.length || 0} cidade(s)`,
      })),
    [plan]
  );

  const applyFilters = () => setFilters({ ...pendingFilters });
  const clearFilters = () => {
    const empty = { search: '', area_id: '', periodo_id: '', status: '', data_inicio: '', data_fim: '' };
    setPendingFilters(empty);
    setFilters(empty);
  };

  const totalPago = summary?.total_pago || 0;
  const orcamentoTotal = summary?.valor_total_orcamento || 0;
  const saldo = summary?.saldo || 0;
  const pctConsumido = orcamentoTotal > 0 ? Math.round((totalPago / orcamentoTotal) * 100) : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="financeiro-gastos-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <Receipt className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Gestão de Gastos</h1>
              <p className="text-muted-foreground mt-1">
                Lance pagamentos, anexe recibos/notas e vincule a área, período e cidades.
              </p>
            </div>
          </div>
          <Button onClick={openCreate} data-testid="new-expense-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo gasto
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            icon={Wallet}
            label="Orçamento"
            value={fmtBRL(orcamentoTotal)}
            sub={plan?.name || '—'}
          />
          <KPICard
            icon={Receipt}
            label="Total pago"
            value={fmtBRL(totalPago)}
            sub={`${pctConsumido}% consumido`}
            color={pctConsumido > 100 ? 'text-red-600' : pctConsumido > 80 ? 'text-amber-600' : 'text-primary'}
          />
          <KPICard
            icon={CheckCircle2}
            label="Saldo"
            value={fmtBRL(saldo)}
            color="text-emerald-600"
          />
          <KPICard
            icon={AlertCircle}
            label="Lançamentos"
            value={total}
            sub={summary?.by_status?.pago?.count ? `${summary.by_status.pago.count} pagos` : '—'}
          />
        </div>

        {/* Distribuição por área */}
        {summary?.by_area?.length > 0 && (
          <Card>
            <CardContent className="pt-6 space-y-3">
              <h3 className="font-bold text-sm">Consumo por área</h3>
              {summary.by_area.map((a) => {
                const pct = a.valor_orcado > 0
                  ? Math.round((a.total_gasto / a.valor_orcado) * 100)
                  : null;
                return (
                  <div key={a.area_id || 'sem-area'} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: a.area_cor }}
                        />
                        <span className="font-medium">{a.area_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {a.count} lançamento(s)
                        </span>
                      </span>
                      <span className="font-bold">
                        {fmtBRL(a.total_gasto)}
                        {a.valor_orcado > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            de {fmtBRL(a.valor_orcado)} ({pct}%)
                          </span>
                        )}
                      </span>
                    </div>
                    {a.valor_orcado > 0 && (
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(pct || 0, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-2">
              <FilterIcon className="h-4 w-4 text-muted-foreground" />
              <span className="font-bold text-sm">Filtros</span>
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-expense-filters">
                  Limpar
                </Button>
                <Button size="sm" onClick={applyFilters} data-testid="apply-expense-filters">
                  Filtrar
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
              <div className="lg:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por descrição, fornecedor..."
                  value={pendingFilters.search}
                  onChange={(e) => setPendingFilters({ ...pendingFilters, search: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                  className="pl-9"
                  data-testid="expense-search-input"
                />
              </div>
              <Combobox
                value={pendingFilters.area_id}
                onChange={(v) => setPendingFilters({ ...pendingFilters, area_id: v || '' })}
                options={areaOptions}
                placeholder="Área"
                searchPlaceholder="Buscar área..."
                emptyLabel="Sem áreas"
                testId="expense-filter-area"
              />
              <Combobox
                value={pendingFilters.periodo_id}
                onChange={(v) => setPendingFilters({ ...pendingFilters, periodo_id: v || '' })}
                options={periodoOptions}
                placeholder="Período"
                searchPlaceholder="Buscar período..."
                emptyLabel="Sem períodos"
                testId="expense-filter-periodo"
              />
              <Select
                value={pendingFilters.status || 'all'}
                onValueChange={(v) =>
                  setPendingFilters({ ...pendingFilters, status: v === 'all' ? '' : v })
                }
              >
                <SelectTrigger data-testid="expense-filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={pendingFilters.data_inicio}
                  onChange={(e) => setPendingFilters({ ...pendingFilters, data_inicio: e.target.value })}
                  className="h-9"
                  title="De"
                />
                <Input
                  type="date"
                  value={pendingFilters.data_fim}
                  onChange={(e) => setPendingFilters({ ...pendingFilters, data_fim: e.target.value })}
                  className="h-9"
                  title="Até"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Listagem */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <p className="text-center py-12 text-sm text-muted-foreground">Carregando...</p>
            ) : items.length === 0 ? (
              <p className="text-center py-12 text-sm text-muted-foreground">
                Nenhum gasto encontrado. Use "Novo gasto" para começar.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-2 py-3 font-bold">Data</th>
                      <th className="px-2 py-3 font-bold">Descrição</th>
                      <th className="px-2 py-3 font-bold">Área / Subárea</th>
                      <th className="px-2 py-3 font-bold">Período</th>
                      <th className="px-2 py-3 font-bold">Status</th>
                      <th className="px-2 py-3 font-bold text-right">Valor</th>
                      <th className="px-2 py-3 font-bold text-right w-24">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((e) => (
                      <tr
                        key={e.id}
                        className="border-b border-border hover:bg-muted/30"
                        data-testid={`expense-row-${e.id}`}
                      >
                        <td className="px-2 py-3 text-xs">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {formatDateBR(e.data_pagamento)}
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="font-medium">{e.descricao}</div>
                          {e.fornecedor && (
                            <div className="text-xs text-muted-foreground">{e.fornecedor}</div>
                          )}
                          {e.fornecedor_documento && (
                            <div className="text-[11px] text-muted-foreground font-mono">
                              {(e.fornecedor_tipo === 'pj' ? 'CNPJ ' : 'CPF ') +
                                maskDoc(e.fornecedor_documento, e.fornecedor_tipo)}
                            </div>
                          )}
                          {e.anexos?.length > 0 && (
                            <div className="text-xs text-emerald-700 flex items-center gap-1 mt-0.5">
                              <Paperclip className="h-3 w-3" /> {e.anexos.length} anexo(s)
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-3 text-sm">
                          {e.area_name && (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ background: e.area_cor || '#94a3b8' }}
                              />
                              {e.area_name}
                            </span>
                          )}
                          {e.subarea_name && (
                            <div className="text-xs text-muted-foreground">{e.subarea_name}</div>
                          )}
                          {!e.area_name && <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-2 py-3 text-sm">
                          {e.periodo_name || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-2 py-3">
                          <Badge variant="outline" className={STATUS_COLORS[e.status]}>
                            {STATUS_OPTIONS.find((s) => s.value === e.status)?.label || e.status}
                          </Badge>
                        </td>
                        <td className="px-2 py-3 text-right font-bold">{fmtBRL(e.valor)}</td>
                        <td className="px-2 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(e)}
                            data-testid={`edit-expense-${e.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            onClick={() => deleteExpense(e)}
                            data-testid={`delete-expense-${e.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar gasto' : 'Novo gasto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-4">
                <Label className="text-xs text-muted-foreground">Descrição *</Label>
                <Input
                  value={form.descricao}
                  onChange={(e) => updateForm({ descricao: e.target.value })}
                  placeholder="Ex: Impressão de santinhos"
                  data-testid="expense-descricao"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-muted-foreground">Valor (R$) *</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.valor}
                  onChange={(e) => updateForm({ valor: parseFloat(e.target.value) || 0 })}
                  data-testid="expense-valor"
                />
                <p className="text-xs text-muted-foreground mt-1">{fmtBRL(form.valor)}</p>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-muted-foreground">Fornecedor / pagador</Label>
                <Input
                  value={form.fornecedor}
                  onChange={(e) => updateForm({ fornecedor: e.target.value })}
                  placeholder="Ex: Gráfica Central LTDA"
                  data-testid="expense-fornecedor"
                />
              </div>
              <div className="md:col-span-3 grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Select
                    value={form.fornecedor_tipo || 'pf'}
                    onValueChange={(v) =>
                      updateForm({ fornecedor_tipo: v, fornecedor_documento: '' })
                    }
                  >
                    <SelectTrigger data-testid="expense-fornecedor-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pf">PF</SelectItem>
                      <SelectItem value="pj">PJ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">
                    {docTipoLabel[form.fornecedor_tipo || 'pf']}
                  </Label>
                  <Input
                    value={maskDoc(form.fornecedor_documento, form.fornecedor_tipo)}
                    onChange={(e) =>
                      updateForm({ fornecedor_documento: e.target.value })
                    }
                    placeholder={
                      form.fornecedor_tipo === 'pj'
                        ? '00.000.000/0000-00'
                        : '000.000.000-00'
                    }
                    data-testid="expense-fornecedor-doc"
                  />
                </div>
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs text-muted-foreground">Data do pagamento</Label>
                <Input
                  type="date"
                  value={form.data_pagamento}
                  onChange={(e) => updateForm({ data_pagamento: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={(v) => updateForm({ status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-3">
                <Label className="text-xs text-muted-foreground">Área do orçamento</Label>
                <Combobox
                  value={form.area_id}
                  onChange={(v) => updateForm({ area_id: v || '', subarea_id: '' })}
                  options={areaOptions}
                  placeholder="Selecione a área"
                  emptyLabel="Sem áreas"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-muted-foreground">Subárea</Label>
                <Combobox
                  value={form.subarea_id}
                  onChange={(v) => updateForm({ subarea_id: v || '' })}
                  options={subareaOptions}
                  placeholder={form.area_id ? 'Selecione a subárea (opcional)' : 'Selecione a área primeiro'}
                  emptyLabel="Sem subáreas"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-muted-foreground">Período</Label>
                <Combobox
                  value={form.periodo_id}
                  onChange={(v) => updateForm({ periodo_id: v || '' })}
                  options={periodoOptions}
                  placeholder="Selecione o período"
                  emptyLabel="Sem períodos"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-muted-foreground">Grupo de cidades</Label>
                <Combobox
                  value={form.city_group_id}
                  onChange={(v) => updateForm({ city_group_id: v || '' })}
                  options={cityGroupOptions}
                  placeholder="Selecione o grupo"
                  emptyLabel="Sem grupos"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs text-muted-foreground">Forma de pagamento</Label>
                <Select
                  value={form.forma_pagamento}
                  onValueChange={(v) => updateForm({ forma_pagamento: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-muted-foreground">Tipo de documento</Label>
                <Select
                  value={form.documento_tipo}
                  onValueChange={(v) => updateForm({ documento_tipo: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-muted-foreground">Nº documento</Label>
                <Input
                  value={form.documento_numero}
                  onChange={(e) => updateForm({ documento_numero: e.target.value })}
                  placeholder="Ex: NF 12345"
                />
              </div>

              <div className="md:col-span-6">
                <Label className="text-xs text-muted-foreground">Observações</Label>
                <Textarea
                  rows={2}
                  value={form.observacoes}
                  onChange={(e) => updateForm({ observacoes: e.target.value })}
                  placeholder="Detalhes adicionais (opcional)"
                />
              </div>

              <div className="md:col-span-6">
                <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Paperclip className="h-3.5 w-3.5" /> Anexos (recibos, NF, fotos)
                </Label>
                {folder ? (
                  <FileMultiUpload
                    value={form.anexos}
                    onChange={(arr) => updateForm({ anexos: arr })}
                    folder={folder}
                    testId="expense-attachments"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Selecione um cliente para anexar arquivos.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-expense-btn">
              {editingId ? 'Salvar alterações' : 'Cadastrar gasto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

const KPICard = ({ icon: Icon, label, value, sub, color }) => (
  <Card>
    <CardContent className="pt-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 truncate ${color || ''}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <Icon className="h-5 w-5 text-primary shrink-0" />
      </div>
    </CardContent>
  </Card>
);
